import { useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Skeleton } from '@/components/ui/Skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useAlertDetail } from './hooks/useAlertDetail'
import { AlertHeader } from './components/detail/AlertHeader'
import { SlaBanner } from './components/detail/SlaBanner'
import { WhySection } from './components/detail/WhySection'
import { WhatSection } from './components/detail/WhatSection'
import { WhoTabs } from './components/detail/WhoTabs'
import { SuggestedGroupings } from './components/detail/SuggestedGroupings'
import { NotesTimeline } from './components/detail/NotesTimeline'
import { StatusHistoryTimeline } from './components/detail/StatusHistoryTimeline'
import { ActionRail } from './components/detail/ActionRail'
import { TriggeredActionPanel } from './components/detail/TriggeredActionPanel'
import { AlertScreeningPanel } from './components/detail/AlertScreeningPanel'

/**
 * AF-126 — alert detail page.
 *
 * Two-column layout: reading column on the left, sticky action rail on
 * the right. The reading column streams in priority order (header + SLA →
 * Why → What → Who → Trail) so analysts can start deciding before the page
 * is fully loaded.
 */
export function AlertDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: alert, isLoading, isError, error, refetch } = useAlertDetail(id)
  const notesRef = useRef<HTMLDivElement | null>(null)

  if (isLoading) return <DetailSkeleton />
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Couldn't load alert</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-fg">{error.message}</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }
  if (!alert) return null

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      <div className="flex min-w-0 flex-col gap-4">
        <SlaBanner sla={alert.sla} status={alert.status} />
        <AlertHeader alert={alert} />
        <WhySection alert={alert} />
        {/* AF-SS-072: Sanctions/PEP screening explainability. Renders
            only when the alert has attached screening matches; on
            other alerts (rule-driven, ML, velocity) the panel
            silently returns null. */}
        <AlertScreeningPanel alertID={alert.id} />
        <TriggeredActionPanel alertId={alert.id} />
        <SuggestedGroupings alertId={alert.id} anchorAlert={alert} />
        <WhatSection transactionId={alert.transaction_id} />
        <WhoTabs relatedAlertIds={alert.related_alert_ids} />
        <NotesTimeline ref={notesRef} alertId={alert.id} notes={alert.notes} />
        <StatusHistoryTimeline history={alert.status_history} />
      </div>
      <div className="lg:sticky lg:top-4 lg:self-start">
        <ActionRail
          alert={alert}
          onJumpToAddNote={() => notesRef.current?.scrollIntoView({ behavior: 'smooth' })}
        />
      </div>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  )
}
